import React, { useState } from 'react';
import { Share2, Facebook, MessageCircle, Instagram, Copy, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Per-track / per-release share menu.
 *
 * Options exposed:
 *   1. Facebook post — Facebook Sharer intent
 *   2. Messenger DM — fb-messenger:// deep link with web fallback (send.me)
 *   3. Instagram DM — mobile Instagram share intent w/ copy-link fallback
 *   4. Copy link — clipboard
 *
 * IG Story visualizer (30-sec MP4 generator) is queued for Phase 2 and NOT wired here yet;
 * once the backend endpoint lands, add a fourth button to this menu.
 */
const buildCaption = ({ title, artist, bpm, key, releaseType }) => {
  const bits = [];
  if (bpm) bits.push(`${bpm} BPM`);
  if (key) bits.push(`Key ${key}`);
  const meta = bits.length ? ` — ${bits.join(' · ')}` : '';
  const kind = releaseType === 'free-download' ? 'Free drop' : 'Fresh cut';
  return `${kind}: ${title}${artist ? ` — ${artist}` : ''}${meta}. Grab it on Kehele Keff Music.`;
};

const isMobile = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const ShareTrackButton = ({
  title,
  artist,
  bpm,
  musicalKey,
  releaseType,
  shareUrl,
  compact = false,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const caption = buildCaption({ title, artist, bpm, key: musicalKey, releaseType });
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedCaption = encodeURIComponent(caption);

  const shareFacebook = () => {
    // Facebook Sharer picks up og:image + og:title from the URL — the caption comes
    // through the `quote` param on desktop and is prefilled for the user to edit.
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedCaption}`,
      'fb-share', 'width=680,height=560,noopener'
    );
    setOpen(false);
  };

  const shareMessenger = () => {
    // fb-messenger:// works when Messenger is installed on mobile; on desktop we fall
    // back to messenger.com/send which prompts the sender to pick a recipient.
    const mob = `fb-messenger://share?link=${encodedUrl}`;
    const web = `https://www.messenger.com/t/?link=${encodedUrl}`;
    window.open(isMobile() ? mob : web, '_blank', 'noopener');
    setOpen(false);
  };

  const shareInstagram = async () => {
    // Instagram doesn't expose a public web share URL. Best UX we can offer:
    //  - Mobile with Web Share API → native picker (may show IG)
    //  - Otherwise: copy the caption+URL and open Instagram DMs
    const shareText = `${caption}\n${shareUrl}`;
    if (isMobile() && navigator.share) {
      try {
        await navigator.share({ title, text: caption, url: shareUrl });
        setOpen(false);
        return;
      } catch { /* user cancelled — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success('Copied — paste into Instagram DM');
    } catch {
      toast.error('Copy failed — long-press to copy manually');
    }
    window.open('https://www.instagram.com/direct/inbox/', '_blank', 'noopener');
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
    setOpen(false);
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(v => !v); }}
        className={`inline-flex items-center gap-1 rounded-full text-[#71717A] hover:text-[#00F0FF] transition ${compact ? 'p-1' : 'px-2 py-1'}`}
        title="Share this track"
        data-testid={testId || 'share-track-btn'}
        aria-expanded={open}
      >
        <Share2 size={compact ? 14 : 16} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          />
          <div
            className="absolute right-0 top-full mt-1 z-50 w-56 bg-[#0F0F11] border border-[#27272A] rounded-lg shadow-xl overflow-hidden text-left"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-menu"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272A] bg-[#121214]">
              <span className="text-[10px] uppercase tracking-wider text-[#71717A]">Share</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[#71717A] hover:text-white"
                aria-label="Close share menu"
              >
                <X size={12} />
              </button>
            </div>
            <button
              type="button"
              onClick={shareFacebook}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#18181B] text-sm text-white"
              data-testid="share-facebook"
            >
              <Facebook size={14} className="text-[#1877F2]" /> Facebook post
            </button>
            <button
              type="button"
              onClick={shareMessenger}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#18181B] text-sm text-white"
              data-testid="share-messenger"
            >
              <MessageCircle size={14} className="text-[#00B2FF]" /> Messenger DM
            </button>
            <button
              type="button"
              onClick={shareInstagram}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#18181B] text-sm text-white"
              data-testid="share-instagram"
            >
              <Instagram size={14} className="text-[#E1306C]" /> Instagram DM
            </button>
            <button
              type="button"
              onClick={copyLink}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#18181B] text-sm text-white border-t border-[#27272A]"
              data-testid="share-copy"
            >
              <Copy size={14} className="text-[#A1A1AA]" /> Copy link
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ShareTrackButton;
